import React, { useMemo, useState } from 'react';

import { useCourseData, CourseMovie } from '../hooks/useCourseData';
import { useContentCourse } from '../context/ContentContext';
import apiBackoffice from '../../../../services/apiBackoffice';
import { useToast } from '../../../../hooks/toast';
import { useAdminDelete } from '../../Admin/hooks/useAdminDelete';
import {
  ContentEditorLayout,
  Confirm,
  useContentEditor,
} from '../../../../components/Admin';
import Field from '../../../../components/Admin/Field';
import { TextInput, TextArea } from '../../../../components/Admin/Field/styles';

interface VideoValues {
  title: string;
  description: string;
  url: string;
  thumb: string;
  videoduration: string;
}

const EMPTY: VideoValues = {
  title: '',
  description: '',
  url: '',
  thumb: '',
  videoduration: '',
};

const DURATION_PATTERN = /^(\d{1,2}:)?\d{1,2}:\d{2}$/;

/**
 * backend-patches/movie-add publicado em 18/10 (NL_uploadVideosToDynamo,
 * CodeSha256 UVALgpUQ…): a Lambda aceita a posição 0 e preserva os atributos
 * que não conhece. Voltar para `false` se a Lambda for revertida — aí editar
 * essas aulas volta a ser bloqueado.
 */
const MOVIE_ADD_PRESERVES_FIELDS = true;

/**
 * Próximo `movieid` no formato que o módulo já usa: curto e com zeros à
 * esquerda ("01", "02"...) ou o legado `curso_modulo_posição`. A Lambda monta a
 * chave como `curso_modulo_movieid`, então o legado gera a chave repetida que
 * as aulas antigas já têm — misturar os dois formatos no mesmo módulo, não.
 */
const nextMovieId = (
  movies: CourseMovie[],
  courseId: string,
  seasonId: string,
  nextPosition: number,
) => {
  const ids = movies.map((movie) => movie.movieid);
  const legacy = ids.length > 0 && ids.some((id) => !/^\d+$/.test(id));
  if (legacy) return `${courseId}_${seasonId}_${nextPosition}`;

  const width = Math.max(2, ...ids.map((id) => id.length));
  let next = Math.max(0, ...ids.map(Number)) + 1;
  while (ids.includes(String(next).padStart(width, '0'))) next += 1;
  return String(next).padStart(width, '0');
};

const VideoForm: React.FC = () => {
  const { addToast } = useToast();
  const { courseId } = useContentCourse();
  const {
    seasons,
    movies,
    loadingSeasons,
    loadingMovies,
    selectedCourseId,
    selectedSeasonId,
    handleSeasonChange,
  } = useCourseData(courseId);
  const { deleteItem, deletingId } = useAdminDelete();

  const { values, setField, selectedId, editing, dirty, select, clear, markSaved } =
    useContentEditor<VideoValues>(EMPTY);

  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const moduleOptions = useMemo(
    () =>
      seasons.map((season) => ({
        value: season.seasonid,
        label: season.title,
        hint: season.seasonid,
      })),
    [seasons],
  );

  const pickerItems = useMemo(
    () =>
      movies.map((movie) => ({
        id: movie.movieid,
        label: movie.title,
        hint: `posição ${movie.position ?? '-'}`,
      })),
    [movies],
  );

  const movieKey = (movie: CourseMovie) =>
    movie.courseid_seasonid_movieid ||
    `${selectedCourseId}_${selectedSeasonId}_${movie.movieid}`;

  const onModuleChange = (seasonId: string) => {
    clear();
    handleSeasonChange(seasonId, selectedCourseId);
  };

  const selectedMovie = editing
    ? movies.find((item) => item.movieid === selectedId)
    : undefined;

  // O servidor às vezes devolve a aula sem o link (curso pago lido sem acesso).
  // Nesse caso o campo vazio NÃO significa "sem link" e não pode ser regravado.
  const linkMissing = !!selectedMovie && !selectedMovie.url?.trim();
  const linkError =
    editing && !values.url.trim()
      ? linkMissing
        ? 'O link desta aula não veio do servidor. Cole o link para salvar; em branco, ele seria apagado.'
        : 'Informe o link do vídeo.'
      : undefined;

  // Enquanto a Lambda não preserva campos, editar estas aulas as estragaria.
  const editBlockReason = (() => {
    if (MOVIE_ADD_PRESERVES_FIELDS || !selectedMovie) return undefined;
    if (Number(selectedMovie.position) === 0) {
      return 'É a primeira aula do módulo (posição 0): o servidor a regravaria na posição 1, empatando com a seguinte.';
    }
    if (selectedMovie.urlvertical?.trim() || selectedMovie.isfree === true) {
      return 'Esta aula tem vídeo vertical ou é gratuita: o servidor apagaria essas marcações ao regravar.';
    }
    return undefined;
  })();

  const onSelectItem = (movieId: string) => {
    const movie = movies.find((item) => item.movieid === movieId);
    if (!movie) return;

    select(movieId, {
      title: movie.title ?? '',
      description: movie.description ?? '',
      url: movie.url?.trim() ?? '',
      thumb: movie.thumb?.trim() ?? '',
      videoduration: movie.videoduration ?? '',
    });
  };

  /** Valor original da aula, ou `' '` (o backend não aceita string vazia). */
  const keep = (value?: string) => (value && value.trim() ? value : ' ');

  // `movie/add` regrava a aula inteira: na edição, os campos que o formulário
  // não mostra são reenviados como vieram, em vez de zerados.
  const buildBody = (movieId: string, position: number, original?: CourseMovie) => ({
    courseid_seasonid_movieid: `${selectedCourseId}_${selectedSeasonId}_${movieId}`,
    courseid: selectedCourseId,
    seasonid: selectedSeasonId,
    movieid: movieId,
    title: values.title.trim() || ' ',
    description: values.description.trim() || ' ',
    position,
    url: values.url.trim() || ' ',
    m3u8: keep(original?.m3u8),
    url_vimeo_backup: keep(original?.url_vimeo_backup),
    thumb: values.thumb.trim() || ' ',
    videoduration: values.videoduration.trim(),
    videotype: original?.videotype?.trim() || 'vimeo',
    exerciseshortmessage: keep(original?.exerciseshortmessage),
    filter: keep(original?.filter),
  });

  const validate = () => {
    if (!selectedCourseId || !selectedSeasonId) {
      addToast({
        type: 'error',
        title: 'Selecione o módulo',
        description: 'Escolha o curso no topo e o módulo antes de salvar.',
      });
      return false;
    }
    if (!values.title.trim()) {
      addToast({ type: 'error', title: 'Título obrigatório' });
      return false;
    }
    // GetCourse.getTotalHours faz parseFloat da duração: vazio derruba /course.
    if (!DURATION_PATTERN.test(values.videoduration.trim())) {
      addToast({
        type: 'error',
        title: 'Duração inválida',
        description: 'Use MM:SS ou HH:MM:SS (ex.: 31:53 ou 01:05:20).',
      });
      return false;
    }
    if (editing && editBlockReason) {
      addToast({
        type: 'error',
        title: 'Edição desta aula indisponível por enquanto',
        description: `${editBlockReason} Aguarde a correção no servidor.`,
      });
      return false;
    }
    // Editar com o link vazio apagaria o vídeo da aula para os alunos.
    if (linkError) {
      addToast({
        type: 'error',
        title: 'Link do vídeo obrigatório',
        description: linkError,
      });
      return false;
    }
    return true;
  };

  const persist = async (movieId: string, position: number, original?: CourseMovie) => {
    setSaving(true);
    try {
      const response = await apiBackoffice.post(
        '/backoffice/movie/add',
        buildBody(movieId, position, original),
      );
      const ok =
        response.status === 200 || response.data === 'OK' || response.data?.message === 'OK';

      if (!ok) throw new Error('O servidor não confirmou a gravação.');

      addToast({ type: 'success', title: editing ? 'Aula atualizada' : 'Aula cadastrada' });
      markSaved();
      await handleSeasonChange(selectedSeasonId, selectedCourseId);
      return true;
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Erro ao salvar a aula',
        description: err?.response?.data?.error || err?.message || 'Tente novamente.',
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const onSave = async () => {
    if (!validate()) return;

    if (editing) {
      if (!selectedMovie) return;
      // A posição 0 é válida (primeira aula); `|| 1` a jogava para a posição 1.
      const position = Number(selectedMovie.position);
      await persist(selectedId, Number.isFinite(position) ? position : 1, selectedMovie);
      return;
    }

    // Novo: a posição é a próxima livre no módulo.
    const nextPosition =
      movies.reduce((max, movie) => Math.max(max, Number(movie.position) || 0), 0) + 1;
    const movieId = nextMovieId(movies, selectedCourseId, selectedSeasonId, nextPosition);
    const ok = await persist(movieId, nextPosition);
    if (ok) clear();
  };

  const onDelete = async () => {
    setConfirmDelete(false);
    const movie = movies.find((item) => item.movieid === selectedId);
    if (!movie) return;

    const done = await deleteItem({
      entity: 'lesson',
      id: movieKey(movie),
      successTitle: 'Aula excluída',
    });

    if (done) {
      clear();
      await handleSeasonChange(selectedSeasonId, selectedCourseId);
    }
  };

  return (
    <>
      <ContentEditorLayout
        moduleOptions={moduleOptions}
        moduleId={selectedSeasonId}
        onModuleChange={onModuleChange}
        modulesLoading={loadingSeasons}
        pickerItems={pickerItems}
        pickerTitle="Aulas deste módulo"
        selectedItemId={selectedId}
        onSelectItem={onSelectItem}
        pickerLoading={loadingMovies}
        editing={editing}
        dirty={dirty}
        saving={saving}
        deleting={!!deletingId}
        onSave={onSave}
        onEdit={onSave}
        onDelete={() => setConfirmDelete(true)}
        onClear={clear}
      >
        <Field label="Título da aula" required>
          <TextInput
            value={values.title}
            onChange={(event) => setField('title', event.target.value)}
            placeholder="Ex.: Agentes internos — endógenos"
          />
        </Field>

        <Field label="Duração" required hint="MM:SS ou HH:MM:SS">
          <TextInput
            value={values.videoduration}
            onChange={(event) => setField('videoduration', event.target.value)}
            placeholder="31:53"
          />
        </Field>

        <Field label="Link do vídeo" hint="Vimeo ou HLS" required={editing} error={linkError}>
          <TextInput
            value={values.url}
            onChange={(event) => setField('url', event.target.value)}
            placeholder="https://vimeo.com/..."
          />
        </Field>

        <Field label="Capa da aula" hint="URL da imagem">
          <TextInput
            value={values.thumb}
            onChange={(event) => setField('thumb', event.target.value)}
            placeholder="https://..."
          />
        </Field>

        <Field label="Descrição" full>
          <TextArea
            value={values.description}
            onChange={(event) => setField('description', event.target.value)}
            placeholder="Resumo da aula"
          />
        </Field>
      </ContentEditorLayout>

      <Confirm
        open={confirmDelete}
        danger
        title="Excluir aula"
        description={`A aula "${values.title}" some do curso para os alunos. Não dá para desfazer.`}
        confirmLabel="Excluir"
        loading={!!deletingId}
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
};

export default VideoForm;
